//
//  Level.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 24, 2026
//  Author: Bob Keathley
//
//  One level Notebook (§6v): Corporate, a Team or a Project. Its rules by
//  source, the advisory text (labelled not enforced), versions, and Preview
//  and Birth and sign for the allowed signer only.
//
import { useCallback, useEffect, useState } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import {
  canSignLevel, levelErrorText, levelLabel, nodePath, notebookPath, previewPath, sourceLabel, versionsPath,
  type AdvisoryItem, type LevelBirth, type LevelKind, type LevelNotebook, type LevelPreview, type LevelSource, type LevelVersion,
} from '@shared/api/levels'
import type { Session } from '@shared/auth'
import { href } from '@shared/router'
import { when } from './Projects'

const short = (s: string | null | undefined) => (s ?? '').slice(0, 12)
export const signerName = (s: LevelVersion['signed_by']) => (s === null ? '—' : typeof s === 'string' ? short(s) : s.name ?? short(s.user_id))

/** Every rule, and the scanned file or answer it comes from. */
export function SourcesTable({ sources: raw }: { sources?: LevelSource[] | null }) {
  const sources = raw ?? []
  return (
    <table>
      <thead><tr><th>Rule</th><th>Comes from</th></tr></thead>
      <tbody>
        {sources.length === 0 && <tr><td colSpan={2} className="hint">No rules at this level yet.</td></tr>}
        {sources.map((s, i) => (
          <tr key={i}>
            <td className="mono">{s.key}</td>
            <td>{sourceLabel(s.source)}{s.origin ? <> <span className="hint">({s.origin})</span></> : null}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Prose from scanned files. Shown and stored, never enforced. */
export function AdvisoryList({ items: rawItems, unassigned }: { items?: AdvisoryItem[] | null; unassigned?: unknown[] | null }) {
  const items = rawItems ?? []
  return (
    <>
      <h3>Advisory text <span className="pill off">advisory, not enforced</span></h3>
      <p className="hint">This is prose from scanned files. It is shown here and shipped for reading, but it is never compiled into a rule and nothing enforces it.</p>
      {items.length === 0 && <p className="hint">No advisory text at this level.</p>}
      {items.map((a, i) => (
        <details key={`${a.sha256 ?? ''}${i}`}>
          <summary><span className="mono">{a.path_rel ?? a.source ?? 'unknown file'}</span> <span className="hint">· {a.scope ?? 'unknown'}{a.depth != null ? ` (${a.depth} up)` : ''}</span></summary>
          <pre className="body">{a.text ?? ''}</pre>
        </details>
      ))}
      {unassigned && unassigned.length > 0 && (
        <p className="hint">{unassigned.length} scanned item{unassigned.length === 1 ? '' : 's'} sit above a project that has no team, so nothing is applied for {unassigned.length === 1 ? 'it' : 'them'}.</p>
      )}
    </>
  )
}

export function VersionsTable({ node, token, refreshKey }: { node: string; token: string; refreshKey: number }) {
  const [items, setItems] = useState<LevelVersion[] | 'error' | null>(null)
  useEffect(() => {
    let alive = true
    setItems(null)
    api<{ items: LevelVersion[] }>(versionsPath(node), {}, token).then((r) => {
      if (!alive) return
      setItems(isOk(r) ? r.data.items : r.error.code === 'not_found' ? [] : 'error')
    })
    return () => { alive = false }
  }, [node, token, refreshKey])
  if (items === null) return <p className="hint">Loading…</p>
  if (items === 'error') return <p className="err" role="alert">The versions could not be loaded.</p>
  return (
    <table>
      <thead><tr><th>Version</th><th>Signed by</th><th>When</th><th>Made from</th><th>Hash</th></tr></thead>
      <tbody>
        {items.length === 0 && <tr><td colSpan={5} className="hint">Not born yet, so there are no versions.</td></tr>}
        {items.map((v, i) => (
          <tr key={v.birth_cert_id}>
            <td className="mono">{v.version}{i === 0 && <> <span className="pill ok">current</span></>}</td>
            <td>{signerName(v.signed_by)}</td>
            <td className="hint">{when(v.signed_at)}</td>
            <td>{v.source}</td>
            <td className="mono hint" title={v.compiled_hash}>{short(v.compiled_hash)}…</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** The would-be mandate from a preview. */
export function PreviewView({ pv }: { pv: LevelPreview }) {
  return (
    <div role="status">
      <h4>Would become version {pv.next_version} <span className="hint mono" title={pv.compiled_hash}>{short(pv.compiled_hash)}…</span></h4>
      {pv.unchanged && <p className="hint">This gives exactly the Notebook that is already current, so there is nothing to sign.</p>}
      <pre className="body">{JSON.stringify(pv.compiled_mandate ?? {}, null, 2)}</pre>
      <h4>Where each rule comes from</h4>
      <SourcesTable sources={pv.sources} />
    </div>
  )
}

export default function Level({ session, kind, refId }: { session: Session; kind: Exclude<LevelKind, 'user'>; refId: string | null }) {
  const node = nodePath(kind, refId)
  const token = session.token
  const mayBirth = canSignLevel(session.user.role, kind)
  const [nb, setNb] = useState<LevelNotebook | null>(null)
  const [name, setName] = useState<string | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [preview, setPreview] = useState<LevelPreview | null>(null)
  const [busy, setBusy] = useState<'preview' | 'sign' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [signed, setSigned] = useState<LevelBirth | null>(null)
  const [vKey, setVKey] = useState(0)

  const load = useCallback(async () => {
    const r = await api<LevelNotebook>(notebookPath(node), {}, token)
    if (isOk(r)) { setNb(r.data); setLoadErr(null) } else setLoadErr(r.error.code === 'not_found' ? 'This does not exist.' : levelErrorText(r.error))
  }, [node, token])
  useEffect(() => { setNb(null); setPreview(null); setSigned(null); setError(null); load() }, [load])
  useEffect(() => {
    if (kind === 'corporate') { setName('Corporate'); return }
    let alive = true
    api<{ name: string }>(kind === 'team' ? `/levels/teams/${refId}` : `/projects/${refId}`, {}, token).then((r) => { if (alive) setName(isOk(r) ? r.data.name : null) })
    return () => { alive = false }
  }, [kind, refId, token])

  async function runPreview() {
    setBusy('preview'); setError(null); setSigned(null)
    const r = await api<LevelPreview>(previewPath(node), { method: 'POST', body: '{}' }, token)
    setBusy(null)
    if (!isOk(r)) { setPreview(null); setError(levelErrorText(r.error)); return }
    setPreview(r.data)
  }
  async function sign() {
    setBusy('sign'); setError(null)
    const r = await api<LevelBirth>(notebookPath(node), { method: 'PUT', body: '{}' }, token)
    setBusy(null)
    if (!isOk(r)) { setError(levelErrorText(r.error)); return }
    setSigned(r.data); setPreview(null); setVKey((k) => k + 1); load()
  }

  if (loadErr) return <div className="hierarchy"><p className="err" role="alert">{loadErr}</p><p><a href={href('/hierarchy')}>‹ Hierarchy</a></p></div>
  if (!nb) return <div className="hierarchy"><p className="hint">Loading…</p></div>

  const projectLink = kind === 'project' && refId ? <> · <a href={href(`/projects/${refId}`)}>Open the project</a></> : null
  return (
    <div className="hierarchy">
      <p className="hint"><a href={href('/hierarchy')}>‹ Hierarchy</a></p>
      <h2>{levelLabel(kind)}{kind !== 'corporate' && name ? `: ${name}` : ''}</h2>
      <p className="hint">
        {nb.notebook_id
          ? <>Notebook <span className="mono">{nb.notebook_id}</span> · version {nb.version}{nb.signer && <> · signed {when(nb.signer.at)}</>}</>
          : 'Not born yet. Preview shows what the first version would hold.'}
        {projectLink}
      </p>
      {error && <p className="err" role="alert">{error}</p>}
      {signed && <p role="status">Signed version {signed.version}. {signed.stale_marked} effective Notebook{signed.stale_marked === 1 ? '' : 's'} marked stale: re-birth them from each project.</p>}

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Rules, by where they come from</h3>
        <SourcesTable sources={nb.sources} />
      </div>
      <div className="panel"><AdvisoryList items={nb.advisory} unassigned={nb.unassigned} /></div>

      {mayBirth ? (
        <div className="panel">
          <h3 style={{ marginTop: 0 }}>Birth and sign</h3>
          <p className="hint">Rules start from the scanned files that sit at this level. Nothing is filled in by hand.</p>
          <p>
            <button className="btn" disabled={busy !== null} onClick={runPreview}>{busy === 'preview' ? 'Previewing…' : 'Preview'}</button>{' '}
            <button className="btn" disabled={busy !== null || !preview || preview.unchanged || !preview.can_sign} onClick={sign}>
              {busy === 'sign' ? 'Signing…' : `Birth and sign version ${preview?.next_version ?? (nb.version ?? 0) + 1}`}
            </button>
            {preview && !preview.can_sign && <span className="hint"> You may not sign this: {preview.cannot_sign_reason === 'own_notebook' ? 'it is your own Notebook.' : 'your role is too low.'}</span>}
            {!preview && <span className="hint"> Preview first to sign.</span>}
          </p>
          {preview && <PreviewView pv={preview} />}
        </div>
      ) : (
        <p className="hint">{kind === 'corporate' ? 'The owner or a security admin signs the Corporate Notebook.' : 'A team admin or above signs this Notebook.'}</p>
      )}

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Versions</h3>
        <VersionsTable node={node} token={token} refreshKey={vKey} />
      </div>
    </div>
  )
}
